import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiShield, FiZap, FiCheckCircle, FiAlertCircle, FiExternalLink } from 'react-icons/fi';
import { userRequest } from '../../utils/requestMethods';
import { useStateContext } from '../../contexts/ContextProvider';
import { format } from 'date-fns';

const PaymentSettings = ({ schoolData, updateSchoolData }) => {
  const { school, user, refreshSchool } = useStateContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cardStatus, setCardStatus] = useState('none'); // 'none', 'pending', 'active', 'expired'

  // The backend saves an authorized card on the school as `card_authorization`;
  // `subscription.authorization` is where older schools had it before the migration.
  const savedCard = school?.card_authorization?.authorization_code
    ? school.card_authorization
    : school?.subscription?.authorization?.authorization_code
      ? school.subscription.authorization
      : null;
  const isPending = Boolean(schoolData?.payment_methods?.pending);

  useEffect(() => {
    // Check if school has existing card authorization
    checkCardStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school, isPending]);

  // Paystack opens in a new tab, so when the admin comes back to this one while a card is
  // pending, reload the school to pick up the card the webhook saved.
  useEffect(() => {
    if (cardStatus !== 'pending') return undefined;
    const handleFocus = () => refreshSchoolData();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardStatus]);

  // Handle callback from Paystack authorization
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const callback = urlParams.get('callback');
    const tab = urlParams.get('tab');
    const schoolId = urlParams.get('schoolId');
    const timestamp = urlParams.get('timestamp');
    
    if (callback === 'success' && tab === 'Payment') {
      // Verify this callback is for the current school and recent
      if (schoolId === school?._id) {
        const callbackTime = parseInt(timestamp);
        const currentTime = Date.now();
        const timeDiff = currentTime - callbackTime;
        
        // Only process callbacks from the last 30 minutes
        if (timeDiff < 30 * 60 * 1000) {
          // User returned from successful authorization
          setSuccess('Welcome back! Please wait while we verify your card authorization status...');
          
          // Check card status after a short delay to allow backend to process
          setTimeout(() => {
            refreshSchoolData();
            // Remove callback parameters from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          }, 2000);
        } else {
          console.warn('Callback timestamp too old, ignoring');
        }
      } else {
        console.warn('Callback schoolId mismatch, ignoring');
      }
    }
  }, [school?._id, school?.subscription]); // Added subscription dependency

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-hide error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const checkCardStatus = async () => {
    try {
      // A saved card counts whatever the subscription's status (trial, expired, ...)
      if (savedCard) {
        setCardStatus('active');
      } else if (isPending) {
        setCardStatus('pending');
      } else {
        setCardStatus('none');
      }
    } catch (error) {
      console.error('Error checking card status:', error);
    }
  };

  const refreshSchoolData = async () => {
    try {
      // Reloading the school re-runs checkCardStatus via the effect above
      if (refreshSchool) {
        await refreshSchool();
      } else {
        checkCardStatus();
      }
    } catch (error) {
      console.error('Error refreshing school data:', error);
    }
  };

  const handleAuthorizeCard = async () => {
    if (!school?._id || !user?.email) {
      setError('School or user information not available');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Construct callback URL to return to this page
      const currentUrl = window.location.href;
      const callbackUrl = `${window.location.origin}/settings?tab=Payment&callback=success&schoolId=${school._id}&timestamp=${Date.now()}`;

      const response = await userRequest.post('/schools/authorize-card', {
        email: user.email,
        schoolId: school._id,
        callback_url: callbackUrl
      });

      // Handle the nested response structure
      if (response.data?.response?.data?.authorization_url) {
        const authData = response.data.response.data;
        const amount = response.data.amount;
        const currency = response.data.currency;
        const reference = response.data.reference;
        
        // Open Paystack authorization in new tab
        window.open(authData.authorization_url, '_blank');
        
        setSuccess(`Card authorization initiated! Amount: ${currency} ${(parseInt(amount) / 100).toFixed(2)}. Please complete the process in the new tab.`);
        setCardStatus('pending');
        
        // Update school data to reflect pending status with additional details
        updateSchoolData({
          payment_methods: {
            ...schoolData?.payment_methods,
            pending: true,
            pending_date: new Date().toISOString(),
            pending_reference: reference,
            pending_amount: amount,
            pending_currency: currency,
            access_code: authData.access_code
          }
        });

        console.log('Authorization initiated:', {
          authorization_url: authData.authorization_url,
          access_code: authData.access_code,
          reference: reference,
          amount: amount,
          currency: currency,
          callback_url: callbackUrl
        });
      } else {
        throw new Error('No authorization URL received from the response');
      }
    } catch (error) {
      console.error('Error authorizing card:', error);
      setError(error.response?.data?.message || 'Failed to initiate card authorization. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getCardStatusInfo = () => {
    switch (cardStatus) {
      case 'active':
        return {
          icon: <FiCheckCircle className="w-6 h-6 text-green-500" />,
          title: 'Payment Card Active',
          description: 'Your payment card is successfully authorized and ready for platform charges.',
          color: 'green',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200'
        };
      case 'pending':
        return {
          icon: <FiAlertCircle className="w-6 h-6 text-yellow-500" />,
          title: 'Authorization Pending',
          description: 'Card authorization is in progress. Please complete the verification in the new tab.',
          color: 'yellow',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200'
        };
      default:
        return {
          icon: <FiCreditCard className="w-6 h-6 text-gray-500" />,
          title: 'No Payment Card',
          description: 'Add a payment card to enable automatic billing for platform services.',
          color: 'gray',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };

  const statusInfo = getCardStatusInfo();

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full mb-4">
          <FiCreditCard className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Settings</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Manage your payment method for platform charges including SMS services, premium features, and usage-based billing.
        </p>
      </div>

      {/* Current Status Card */}
      <div className={`${statusInfo.bgColor} ${statusInfo.borderColor} border-2 rounded-2xl p-6`}>
        <div className="flex items-start space-x-4">
          {statusInfo.icon}
          <div className="flex-1">
            <h3 className={`text-lg font-semibold text-${statusInfo.color}-800 mb-2`}>
              {statusInfo.title}
            </h3>
            <p className={`text-${statusInfo.color}-700 mb-4`}>
              {statusInfo.description}
            </p>
            
            {cardStatus === 'pending' && (
              <div className="flex items-center space-x-2 text-yellow-700">
                <FiExternalLink className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Complete authorization in the new tab to activate your card
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Platform Charges Information */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FiZap className="w-5 h-5 text-blue-600 mr-2" />
          Platform Charges & Billing
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <div>
                <h4 className="font-medium text-gray-900">SMS Services</h4>
                <p className="text-sm text-gray-600">Bulk SMS notifications, alerts, and communications</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              <div>
                <h4 className="font-medium text-gray-900">Premium Features</h4>
                <p className="text-sm text-gray-600">Advanced analytics, custom reports, and integrations</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <h4 className="font-medium text-gray-900">Usage-Based Billing</h4>
                <p className="text-sm text-gray-600">Storage, API calls, and data processing</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-medium text-gray-900 mb-3">Billing Cycle</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Monthly charges:</span>
                <span className="font-medium">Automatically billed</span>
              </div>
              <div className="flex justify-between">
                <span>Payment method:</span>
                <span className="font-medium">Credit/Debit Card</span>
              </div>
              <div className="flex justify-between">
                <span>Security:</span>
                <span className="font-medium">PCI DSS Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security & Trust */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <FiShield className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Secure & Trusted</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div>
                <h4 className="font-medium mb-1">🔒 PCI DSS Compliant</h4>
                <p>Your card information is encrypted and secure</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">🛡️ Paystack Integration</h4>
                <p>Trusted payment processor with bank-grade security</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">📊 Transparent Billing</h4>
                <p>Clear breakdown of all charges and usage</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">⚡ Instant Activation</h4>
                <p>Services activated immediately after payment</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Section */}
      {cardStatus === 'none' && (
        <div className="text-center bg-white border-2 border-dashed border-gray-300 rounded-2xl p-8">
          <FiCreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Get Started?</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Add your payment card to enable automatic billing and unlock all platform features.
          </p>
          
          <button
            onClick={handleAuthorizeCard}
            disabled={isLoading}
            className={`inline-flex items-center px-6 py-3 rounded-xl font-medium text-white transition-all duration-200 shadow-lg transform ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed transform-none'
                : 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8] hover:from-[#2a2a7a] hover:to-[#2a5bb8] hover:shadow-xl hover:scale-105'
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Processing...
              </>
            ) : (
              <>
                <FiCreditCard className="w-5 h-5 mr-2" />
                Add Payment Card
              </>
            )}
          </button>
          
          <p className="text-xs text-gray-500 mt-4">
            You'll be redirected to a secure payment page to complete the authorization
          </p>
        </div>
      )}

      {cardStatus === 'active' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FiCheckCircle className="w-5 h-5 text-green-600 mr-2" />
            Active Payment Method
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-sm text-gray-600">Card</span>
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {[savedCard?.brand || savedCard?.card_type, savedCard?.last4 && `•••• ${savedCard.last4}`]
                    .filter(Boolean)
                    .join(' ') || 'Saved card'}
                </span>
              </div>

              {savedCard?.exp_month && savedCard?.exp_year && (
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <span className="text-sm text-gray-600">Expires</span>
                  <span className="text-sm font-medium text-gray-900">
                    {String(savedCard.exp_month).padStart(2, '0')}/{savedCard.exp_year}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-sm text-gray-600">Bank</span>
                <span className="text-sm font-medium text-gray-900">
                  {savedCard?.bank || 'N/A'}
                </span>
              </div>
              
              {/*<div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="text-sm text-gray-600">Authorization Code</span>
                <span className="text-sm font-mono font-medium text-gray-900">
                  {savedCard?.authorization_code || 'N/A'}
                </span>
              </div>*/}

              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <span className="text-sm text-gray-600">Channel</span>
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {savedCard?.channel || 'N/A'}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-medium text-gray-900 mb-3">Billing Information</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Previous billing:</span>
                  <span className="font-medium">
                    {school?.subscription?.prev_billing_date 
                      ? format(new Date(school.subscription.prev_billing_date), 'MMM dd, yyyy')
                      : 'N/A'
                    }
                  </span>
                </div>
                {/*<div className="flex justify-between">
                  <span>Next billing:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {school?.subscription?.next_billing_date 
                      ? format(new Date(school.subscription.next_billing_date), 'MMM dd, yyyy')
                      : 'N/A'
                    }
                  </span>
                </div>*/}
                <div className="flex justify-between">
                  <span>Billing cycle:</span>
                  <span className="font-medium text-green-600">Per Term</span>
                </div>
                <div className="flex justify-between">
                  <span>Retry attempts:</span>
                  <span className="font-medium">
                    {school?.subscription?.retries || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">Card Details:</p>
                <ul className="space-y-1 text-xs">
                  {/*<li>• Authorization Code: {savedCard?.authorization_code}</li>*/}
                  <li>• Bank: {savedCard?.bank || 'N/A'}</li>
                  <li>• Country: {savedCard?.country_code || 'N/A'}</li>
                  <li>• Reusable: {savedCard?.reusable ? 'Yes' : 'No'}</li>
                </ul>
              </div>
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">Billing Status:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Status: {school?.subscription?.status || 'N/A'}</li>
                  <li>• Email: {school?.subscription?.email || school?.school_email || 'N/A'}</li>
                  <li>• Last Updated: {school?.subscription?.prev_billing_date 
                      ? format(new Date(school.subscription.prev_billing_date), 'MMM dd, yyyy')
                      : 'N/A'
                    }
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {cardStatus === 'pending' && (
        <div className="text-center bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-8">
          <FiAlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-yellow-800 mb-2">Authorization in Progress</h3>
          <p className="text-yellow-700 mb-4 max-w-md mx-auto">
            Please complete the card verification process in the new tab that opened. 
            Once completed, your card will be automatically activated.
          </p>
          
          {/* Show pending authorization details if available */}
          {schoolData?.payment_methods?.pending_reference && (
            <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-4 mb-6 max-w-md mx-auto">
              <h4 className="font-medium text-yellow-800 mb-2">Authorization Details</h4>
              <div className="space-y-2 text-sm text-yellow-700">
                <div className="flex justify-between">
                  <span>Reference:</span>
                  <span className="font-mono font-medium">{schoolData.payment_methods.pending_reference}</span>
                </div>
                {schoolData.payment_methods.pending_amount && (
                  <div className="flex justify-between">
                    <span>Amount:</span>
                    <span className="font-medium">
                      {schoolData.payment_methods.pending_currency} {(parseInt(schoolData.payment_methods.pending_amount) / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                {schoolData.payment_methods.access_code && (
                  <div className="flex justify-between">
                    <span>Access Code:</span>
                    <span className="font-mono font-medium">{schoolData.payment_methods.access_code}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={refreshSchoolData}
              className="inline-flex items-center px-6 py-3 rounded-xl font-medium text-white bg-yellow-600 hover:bg-yellow-700 transition-colors"
            >
              <FiCheckCircle className="w-5 h-5 mr-2" />
              I've finished — check my card
            </button>
            <button
              onClick={handleAuthorizeCard}
              disabled={isLoading}
              className="inline-flex items-center px-6 py-3 rounded-xl font-medium text-yellow-800 bg-yellow-100 hover:bg-yellow-200 transition-colors"
            >
              <FiExternalLink className="w-5 h-5 mr-2" />
              Reopen Authorization
            </button>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="fixed top-5 right-5 bg-green-500 text-white p-4 rounded-xl shadow-lg z-50 max-w-md">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed top-5 right-5 bg-red-500 text-white p-4 rounded-xl shadow-lg z-50 max-w-md">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
